import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export type ClientOnboardingStatus =
  | 'draft' | 'submitted' | 'under_review' | 'commercial_review' | 'sent_back' | 'clarification_required'
  | 'approved' | 'active' | 'inactive' | 'rejected';

export type ClientReviewDecision = 'approve' | 'reject' | 'send_back' | 'request_clarification';

export const BILLING_STRUCTURES = ['site_wise', 'monthly', 'fortnightly', 'milestone', 'project', 'custom'] as const;

export interface ClientContact { id?: number; contact_type?: string; name: string; designation?: string | null; department?: string | null; mobile?: string | null; alternate_mobile?: string | null; email?: string | null; is_primary?: boolean }
export interface ClientDocument { id?: number; document_type: string; file_name?: string | null; file_url?: string | null; doc_status?: string; doc_remarks?: string | null }

export interface ClientApplication {
  id: number;
  application_code: string;
  client_id: number | null;
  status: ClientOnboardingStatus;
  current_stage: string;
  // Company
  company_name: string;
  gst_registered: boolean;
  gst_number: string | null;
  registered_address: string | null;
  pan_number: string | null;
  website: string | null;
  industry_type: string | null;
  logo_url: string | null;
  // Billing
  billing_structure: string | null;
  billing_company_name: string | null;
  billing_address: string | null;
  billing_gst_number: string | null;
  billing_state_id: number | null;
  billing_city: string | null;
  billing_pincode: string | null;
  billing_same_as_registered: boolean;
  // Accounts
  accounts_contact_person: string | null;
  accounts_email: string | null;
  accounts_mobile: string | null;
  accounts_alternate_number: string | null;
  finance_head_name: string | null;
  finance_head_email: string | null;
  finance_head_mobile: string | null;
  // Commercial
  credit_period_days: number | null;
  credit_limit: number | null;
  payment_terms: string | null;
  invoice_submission_process: string | null;
  billing_frequency: string | null;
  tds_applicable: boolean;
  tds_percentage: number | null;
  security_deposit_required: boolean;
  // Declaration
  declaration_name: string | null;
  declaration_designation: string | null;
  declaration_company_name: string | null;
  declaration_date: string | null;
  declaration_accepted: boolean;
  signature_url: string | null;
  seal_url: string | null;
  // Source / progress
  source_type: string | null;
  submitted_role: string | null;
  submitted_at: string | null;
  created_at: string | null;
  completed_steps: number[];
  // Children
  contacts: ClientContact[];
  documents: ClientDocument[];
  // Enriched (grid)
  contact_person?: string | null;
  contact_email?: string | null;
  contact_mobile?: string | null;
  // Admin-only (absent for client role)
  general_review_status?: string | null;
  commercial_review_status?: string | null;
  internal_remarks?: string | null;
  workflow?: Array<{ id: number; stage: string; stage_order: number; status: string; assigned_role: string | null; remarks: string | null }>;
}

export const clientOnboardingApi = {
  async get(id: number): Promise<ClientApplication> {
    return (await apiClient.get<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}`)).data.data;
  },
  async create(payload: Record<string, unknown>): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>('/client-onboarding', payload)).data.data;
  },
  async saveDraft(id: number, payload: Record<string, unknown>): Promise<ClientApplication> {
    return (await apiClient.put<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/draft`, payload)).data.data;
  },
  async submit(id: number): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/submit`, {})).data.data;
  },
  async respondClarification(id: number): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/respond-clarification`, {})).data.data;
  },
  async list(params: Record<string, unknown> = {}): Promise<{ data: ClientApplication[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<ClientApplication[]>>('/client-onboarding', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  async review(id: number, decision: ClientReviewDecision, remarks?: string): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/review`, { decision, remarks })).data.data;
  },
  async withdraw(id: number): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/withdraw`, {})).data.data;
  },
  async remove(id: number): Promise<{ deleted: true }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true }>>(`/client-onboarding/${id}`)).data.data;
  },
  /** Admin/Super-Admin: delete the client master + its portal login too. */
  async adminDelete(id: number): Promise<{ deleted: true; clientId: number | null }> {
    return (await apiClient.delete<ApiSuccess<{ deleted: true; clientId: number | null }>>(`/client-onboarding/${id}/full`)).data.data;
  },
  async clone(id: number): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/clone`, {})).data.data;
  },
  async reopen(id: number): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/reopen`, {})).data.data;
  },
  async changeStatus(id: number, status: 'active' | 'inactive'): Promise<ClientApplication> {
    return (await apiClient.post<ApiSuccess<ClientApplication>>(`/client-onboarding/${id}/status`, { status })).data.data;
  },
};

export interface ClientOnboardingDashboard {
  kpis: Record<string, number>;
  charts: Record<string, Array<{ label: string; value: number }>>;
  tables: {
    recentlySubmitted: Array<Record<string, unknown>>;
    pendingApprovals: Array<Record<string, unknown>>;
  };
}

export const clientDashboardApi = {
  async dashboard(): Promise<ClientOnboardingDashboard> {
    return (await apiClient.get<ApiSuccess<ClientOnboardingDashboard>>('/client-onboarding-dashboard')).data.data;
  },
};
